import type { CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { Logo, cn } from '@clarix/ui';
import { APP_REGISTRY } from '@/gateway/apps';
import { useBootTrace } from '@/gateway/devtools/bootTrace';

/**
 * Loader shown while the shell is bootstrapping the destination app — during
 * /me / onboarding fetches AND during React.lazy chunk loads. Text-free, so it
 * reads as a clean splash rather than a "still working…" interruption.
 *
 * The active app is resolved from the URL, and the loader wears THAT app's
 * identity: its own favicon at the centre (the same mark as the browser tab,
 * the app switcher and the workspace shelf) inside a ring tinted with its
 * `brandColor`. Only at the apex — onboarding, the launcher, no app in the
 * path — does it fall back to the Clarix mark and indigo, because there is no
 * app to be yet. Showing the Clarix logo while loading PMS said "the product
 * is starting" when the truth was "PMS is starting".
 *
 * The runtime colour rides on a CSS var rather than a literal class, the same
 * pattern AppMark uses (UI rule 1).
 *
 * `compact` is the in-app tier: once the chrome is painted, a hand-off between
 * remotes is one panel filling in, not a cold boot, so the same mark renders
 * smaller and without the full-viewport minimum height.
 */
export function AppBootSplash({ compact = false }: { compact?: boolean }) {
  const location = useLocation();
  useBootTrace(compact ? 'AppBootSplash(compact)' : 'AppBootSplash(full)');
  const firstSegment = location.pathname.split('/').filter(Boolean)[0];
  const app = APP_REGISTRY.find((a) => a.slug === firstSegment);
  const accent = { '--boot-accent': app?.brandColor ?? '#4f46e5' } as CSSProperties;

  const markSize = compact ? 28 : 48;

  return (
    <div
      // Stable hook for local dev-loop browser automation (agent-browser). The
      // aria-busy/aria-live pair is NOT unique — AppSkeleton and several list
      // loaders share it — so waits must key off this testid, not the aria attrs.
      data-testid="app-boot-splash"
      aria-busy="true"
      aria-live="polite"
      aria-label={app ? `Loading ${app.name}` : 'Loading'}
      className={cn(
        'flex h-full w-full items-center justify-center',
        compact ? 'min-h-[40vh]' : 'min-h-[60vh]',
      )}
    >
      <div
        className={cn(
          'relative flex items-center justify-center',
          compact ? 'h-14 w-14' : 'h-24 w-24',
        )}
      >
        {/* Ring — a faint full-circle track plus one spinning leading edge, both
            in the app's brand colour. TWO elements, not one, because the single
            `border-current/15` this replaced DOES NOT COMPILE (verified against a
            real Tailwind build: zero rules emitted). The track then fell back to
            the default border colour, so the loader span in grey with a single
            tinted edge — the grey spinner. Every class here is one Tailwind
            actually generates. */}
        <span
          style={accent}
          className="absolute inset-0 rounded-full border-2 border-current text-[color:var(--boot-accent)] opacity-20"
        />
        <span
          style={accent}
          className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-current text-[color:var(--boot-accent)]"
        />
        {/* The app's own mark, pulsing inside the ring. `alt=""` because the
            wrapper already carries the "Loading <app>" label — a second
            announcement would just repeat it. */}
        <span className="animate-pulse">
          {app?.favicon ? (
            <img src={app.favicon} alt="" width={markSize} height={markSize} />
          ) : (
            <Logo size={markSize} />
          )}
        </span>
      </div>
    </div>
  );
}
